import React from 'react';
import "../css/Modal.css";

const FOCUSABLE =
    'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

// Stack of currently-mounted modals, innermost last. Modals genuinely nest
// here — anything using useConfirm() renders a ConfirmDialog on top of itself —
// and both the scroll lock and the key handling need to know about it.
const modalStack = [];
let savedBodyOverflow = '';

function pushModal(instance) {
    if (modalStack.length === 0) {
        savedBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
    }
    modalStack.push(instance);
}

function popModal(instance) {
    const i = modalStack.indexOf(instance);
    if (i !== -1) modalStack.splice(i, 1);
    if (modalStack.length === 0) {
        document.body.style.overflow = savedBodyOverflow;
    }
}

const isTopmost = (instance) => modalStack[modalStack.length - 1] === instance;

class Modal extends React.Component {
    constructor(props) {
        super(props);
        this.modalRef = React.createRef();
        this.previouslyFocused = null;
    }

    // Focusable descendants, minus anything inside a hidden subtree. Dialogs
    // that switch between panels (the file-details tab rail) keep the inactive
    // ones mounted with `hidden`, and tabbing into a panel nobody can see is
    // worse than no trap at all. `[hidden]` is checked rather than measured
    // geometry so this stays correct in jsdom and for `position: fixed` children.
    focusableNodes = () => {
        if (!this.modalRef.current) return [];
        return Array.from(this.modalRef.current.querySelectorAll(FOCUSABLE))
            .filter((el) => !el.closest('[hidden]'));
    };

    // L-12: dismiss on Escape, and trap Tab focus inside the dialog (a11y).
    handleKeyDown = (e) => {
        // Every mounted modal listens on `document`, so without this guard one
        // Escape closed the whole stack at once — confirming a destructive
        // action with the keyboard also dismissed the dialog that asked. Only
        // the topmost modal reacts; the ones beneath it stay put.
        if (!isTopmost(this)) return;

        if (e.key === 'Escape') {
            this.props.onClose?.();
            return;
        }
        if (e.key !== 'Tab' || !this.modalRef.current) return;

        const nodes = this.focusableNodes();
        if (nodes.length === 0) {
            e.preventDefault();
            return;
        }
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        const active = document.activeElement;

        if (e.shiftKey && (active === first || !this.modalRef.current.contains(active))) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
        }
    };

    componentDidMount() {
        this.previouslyFocused = document.activeElement;
        pushModal(this);
        document.addEventListener('keydown', this.handleKeyDown);
        // Move focus into the dialog so keyboard users start inside it.
        const node = this.modalRef.current;
        if (node) {
            (this.focusableNodes()[0] || node).focus();
        }
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown);
        popModal(this);
        // Restore focus to whatever was focused before the modal opened.
        if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
            this.previouslyFocused.focus();
        }
    }

    render() {
        const { size, className } = this.props;
        const classes = [
            "modal",
            size ? `modal--${size}` : "",
            className || "",
        ].filter(Boolean).join(" ");

        // Callers label their dialog by passing aria-labelledby / aria-label
        // (ConfirmDialog already did). Those props were being dropped here, so
        // every dialog in the app announced itself as just "dialog". Forward any
        // aria-* the caller supplied onto the element that carries role="dialog".
        const ariaProps = {};
        for (const key of Object.keys(this.props)) {
            if (key.startsWith('aria-')) ariaProps[key] = this.props[key];
        }

        return (
            <>
                <div className="modalbackdrop" onClick={this.props.onClose}></div>
                <div
                    ref={this.modalRef}
                    role="dialog"
                    aria-modal="true"
                    tabIndex={-1}
                    className={classes}
                    {...ariaProps}
                >
                    {this.props.children}
                </div>
            </>
        );
    }
}

export default Modal;
